import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthState } from "@/context";
import { ToastMessage, type } from "@/lib/toastr";
import { ExemptBank, RequireBank } from "@/services/Users.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TagIcon, TagsIcon } from "lucide-react";
import { BsThreeDotsVertical } from "react-icons/bs";

const BankComponent = (bank: {
  accountName: string;
  bankName: string;
  accountNumber: string;
  exemptFromKYC: boolean;
}) => {
  const { state } = useAuthState();
  const queryClient = useQueryClient();
  const mutationExempt = useMutation({
    mutationFn: async (id) => await ExemptBank(id),
    mutationKey: ["exempt"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(
        type.SUCCESS,
        "Customer bank exempted from kyc successfully"
      );
    },
    onError: (err: any) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });
  const mutationRequire = useMutation({
    mutationFn: async (id) => await RequireBank(id),
    mutationKey: ["require"],
    onSuccess: () => {
      queryClient.invalidateQueries("user");
      queryClient.invalidateQueries("bank");

      ToastMessage(type.SUCCESS, "Customer bank kyc required successfully");
    },
    onError: (err: any) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  return (
    <div className="">
      <div className="grid grid-cols-3">
        {bank?.data?.map((item: string, index: number) => (
          <div
            className="flex  gap-1 relative w-[354.67px] bg-off-white-1 border-off-white rounded-[10px] h-[172px] justify-between
          pt-5"
            key={index}
          >
            <div className="flex flex-col justify-between pl-6 mb-5">
              <div className="flex items-start gap-2 ">
                <img
                  src="https://github.com/shadcn.png"
                  className="w-4 h-4 rounded-full"
                />
                <div className="flex flex-col gap-[2px]">
                  <p className="text-sm text-gray-0"> {item?.accountName}</p>
                  <p className="text-xs text-gray-80">{item?.bankName}</p>
                </div>
              </div>
              <div className="flex flex-col gap-[2px]">
                <p className="text-xs text-gray-60 flex items-center gap-[3px]">
                  Account Number
                  {item?.default && (
                    <span
                      className="text-primary font-medium text-[10px] px-[6px] rounded-[8px] h-[18px] bg-[#FFCFFD]
                      flex items-center justify-center
                      "
                    >
                      Default
                    </span>
                  )}
                </p>
                <p className="text-sm text-primary-3"> {item?.accountNumber}</p>
              </div>
            </div>
            <div className="flex flex-col items-end justify-between">
              <div className="me-5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="px-1 py-2" variant="ghost">
                      <span className="sr-only">Open menu</span>
                      <BsThreeDotsVertical />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {item?.exemptFromKYC ? (
                      <DropdownMenuItem
                        onClick={() => mutationRequire.mutate(item?.id)}
                        className="text-primary-9 flex items-center gap-2 text-xs "
                      >
                        <TagIcon />
                        Remove Exemption
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => mutationExempt.mutate(item?.id)}
                        className="text-primary-9 flex items-center gap-2 text-xs "
                      >
                        <TagIcon />
                        Exempt account
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <svg
                width="120"
                height="125"
                viewBox="0 0 120 125"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M44.6783 214.818C35.8663 203.735 23.0519 196.437 13.9491 185.591C4.84628 174.746 0.559895 157.121 10.5483 147.108C17.2066 140.434 27.7939 139.648 37.1087 141.145C45.2727 142.456 54.9317 147.678 53.9614 155.895C52.9203 164.709 40.7255 166.972 32.3186 164.131C11.614 157.126 -0.695874 132.992 3.07206 111.456C6.83999 89.9199 24.5295 72.2865 45.3156 65.5833C66.1017 58.8801 89.2726 62.1681 109.066 71.457C122.897 77.9493 136.048 88.1762 140.956 102.662C147.176 121.021 137.459 142.729 120.566 152.17C103.674 161.612 81.5509 159.305 65.537 148.393C49.5231 137.48 39.507 118.986 36.6606 99.8196C33.3328 77.3961 39.6625 53.3886 54.8892 36.6355C67.4582 22.7996 85.7483 14.34 104.431 13.7151C123.114 13.0902 141.946 20.3145 155.448 33.2761C162.353 39.908 168.101 50.2685 163.583 58.6956C160.139 65.1215 151.808 67.5606 144.609 66.4173C130.669 64.204 120.005 50.2093 120.379 36.0878C120.754 21.9662 131.247 9.04996 144.635 4.58456C158.022 0.119153 173.46 3.68847 184.451 12.5627C195.444 21.4414 202.073 35.1212 203.649 49.1635"
                  stroke="#F3E2F2"
                  stroke-width="4"
                  stroke-miterlimit="10"
                />
              </svg>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BankComponent;
